<template>
    <div class="p-grid p-fluid">
        <template v-for="(item, idx) in parentItem.data" :key="item.id">
            <!-- Subgroup header strip: rendered whenever the subgroup
                 transitions between consecutive items. Full-width so it
                 breaks the 2-column grid flow and the questions below it
                 start on a new row. Only shown when publishGateEnabled is
                 on, so BPO and other untoggled question_sets render flat. -->
            <div
                v-if="publishGateEnabled && shouldShowSubgroupHeader(idx)"
                class="p-col-12 subgroup-header-wrap"
            >
                <div class="subgroup-header">
                    <i v-if="item.subgroup_icon" :class="['pi', item.subgroup_icon, 'subgroup-icon']"></i>
                    <span class="subgroup-label">{{ item.subgroup_name }}</span>
                </div>
            </div>
        <div
            :class="[
                item.field_type === 'structured_table' ? 'p-col-12' : 'p-col-12 p-md-6',
                'answerfield-slot',
                `answerfield-${provenanceState(item)}`,
            ]"
        >
            <ShortTextInputComponent
                v-if="item.field_type === 'short_text'"
                :item="item"
                :label="item.display_name"
                :modelValue="getAnswer(item) || modelValue[item.id]"
                :placeholder="item.placeholder"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :attachmentLink="getAttachmentInfo(item)"
                :disableEdit="disableEdit"
            />
            <NumberInputComponent
                v-if="item.field_type === 'number'"
                :item="item"
                :label="item.display_name"
                :modelValue="getAnswer(item) || modelValue[item.id]"
                :placeholder="item.placeholder"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
            />
            <DecimalInputComponent
                v-if="item.field_type === 'decimal'"
                :item="item"
                :label="item.display_name"
                :modelValue="getAnswer(item) || modelValue[item.id]"
                :placeholder="item.placeholder"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
            />
            <TextAreaComponent
                v-if="item.field_type === 'long_text'"
                :item="item"
                :placeholder="item.placeholder"
                :label="item.display_name"
                :modelValue="getAnswer(item) || modelValue[item.id]"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                :attachmentLink="getAttachmentInfo(item)"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
                :publishGateEnabled="publishGateEnabled"
            />
            <BooleanSelectDropdownComponent
                v-if="item.field_type === 'boolean_select'"
                :options="item.dropdown_options"
                :label="item.display_name"
                :placeholder="item.placeholder"
                :modelValue="getAnswer(item) || modelValue[item.id]"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
            />
            <SingleSelectDropdownComponent
                v-if="item.field_type === 'single_select'"
                :options="prepareOptions(item)"
                :label="item.display_name"
                :placeholder="item.placeholder"
                :modelValue="decorateAnswer(item, getAnswer(item) || modelValue[item.id])"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
                :item="item"
                :publishGateEnabled="publishGateEnabled"
            />
            <SingleSelectDropdownSearchComponent
                v-if="item.field_type === 'single_select_search'"
                :options="prepareOptions(item)"
                :label="item.display_name"
                :placeholder="item.placeholder"
                :modelValue="decorateAnswer(item, getAnswer(item) || modelValue[item.id])"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
                :item="item"
                :publishGateEnabled="publishGateEnabled"
            />
            <MultiSelectDropdownComponent
                v-if="item.field_type === 'multi_select'"
                :options="prepareOptions(item)"
                :label="item.display_name"
                :placeholder="item.placeholder"
                :modelValue="decorateAnswer(item, getAnswer(item) || modelValue[item.id])"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
                :item="item"
                :publishGateEnabled="publishGateEnabled"
                :maxSelections="item.max_selections"
            />
            <MultiSelectDropdownSearchComponent
                v-if="item.field_type === 'multi_select_search'"
                :options="prepareOptions(item)"
                :label="item.display_name"
                :placeholder="item.placeholder"
                :modelValue="decorateAnswer(item, getAnswer(item) || modelValue[item.id])"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
                :item="item"
                :publishGateEnabled="publishGateEnabled"
                :maxSelections="item.max_selections"
            />
            <TreeSelectDropdownComponent
                v-if="item.field_type === 'tree_select'"
                :options="prepareOptions(item)"
                :item="item"
                :label="item.display_name"
                :placeholder="item.placeholder"
                :modelValue="getAnswer(item) || modelValue[item.id]"
                @update:modelValue="(value) => handleInput(item.id, value)"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                @isValid="handleValidation($event, item.id)"
                :disableEdit="disableEdit"
            />

            <FileUploadCompoent
                v-if="item.field_type === 'file_upload'"
                :label="item.display_name"
                @saved="onSaved"
                :isImage="false"
                :isAttachment="true"
                :fieldName="item.field_name"
                :fieldId="item.id"
                :logoUrl="getAttachmentInfo(item)"
                :disableEdit="disableEdit"
            />
            <!-- Structured table: row-label tables with dropdown percentage
                 ranges (e.g. Engineering Workforce Profile's 4 tables).
                 modelValue is an object for static shapes and an array for
                 the dynamic country-row shape; the renderer reads
                 dropdown_options to know which to use. -->
            <StructuredTableComponent
                v-if="item.field_type === 'structured_table'"
                :item="item"
                :label="item.display_name"
                :modelValue="getAnswer(item) || modelValue[item.id]"
                :validation_rules="item.validation_rules"
                :error_message="item.error_message"
                :required="!!item.required"
                :disableEdit="disableEdit"
                @update:modelValue="(value) => handleInput(item.id, value)"
                @isValid="handleValidation($event, item.id)"
                :publishGateEnabled="publishGateEnabled"
            />
            <!-- AI Pre-Population footer: "AI inferred from X" (amber
                 state) or "AI was unable to answer" (red state). Renders
                 nothing for plain / dashed-recommended states. -->
            <div
                v-if="provenanceFooter(item)"
                class="answerfield-provenance-footer"
                :class="`answerfield-footer-${provenanceState(item)}`"
            >
                <span class="answerfield-footer-icon">
                    <template v-if="provenanceState(item) === 'ai-inferred' && !getAnswerRow(item)?._locallyEdited && ['ai_inferred', 'ai_extracted'].includes(getAnswerRow(item)?.source)">✦</template>
                    <template v-else-if="provenanceState(item) === 'ai-confirmed'">✓</template>
                    <template v-else>!</template>
                </span>
                {{ provenanceFooter(item) }}
            </div>
        </div>
        </template>
    </div>
</template>

<script>
import DecimalInputComponent from '@/components/answerBank/DecimalInputComponent';
import TextAreaComponent from '@/components/answerBank/TextAreaComponent';
import SingleSelectDropdownComponent from '@/components/answerBank/SingleSelectDropdownComponent';
import SingleSelectDropdownSearchComponent from '@/components/answerBank/SingleSelectDropdownSearchComponent';
import MultiSelectDropdownComponent from '@/components/answerBank/MultiSelectDropdownComponent';
import MultiSelectDropdownSearchComponent from '@/components/answerBank/MultiSelectDropdownSearchComponent';
import TreeSelectDropdownComponent from '@/components/answerBank/TreeSelectDropdownComponent';
import ShortTextInputComponent from '@/components/answerBank/ShortTextInputComponent';
import NumberInputComponent from '@/components/answerBank/NumberInputComponent';
import BooleanSelectDropdownComponent from '@/components/answerBank/BooleanSelectDropdownComponent';
import FileUploadCompoent from './FileUploadCompoent.vue';
import StructuredTableComponent from '@/components/answerBank/StructuredTableComponent';

export default {
    name: 'Wrapper',
    components: {
        ShortTextInputComponent,
        NumberInputComponent,
        DecimalInputComponent,
        TextAreaComponent,
        SingleSelectDropdownComponent,
        SingleSelectDropdownSearchComponent,
        MultiSelectDropdownComponent,
        MultiSelectDropdownSearchComponent,
        TreeSelectDropdownComponent,
        FileUploadCompoent,
        BooleanSelectDropdownComponent,
        StructuredTableComponent,
    },
    props: {
        parentItem: {
            type: Object,
            required: true,
        },
        modelValue: {
            type: Object,
            required: true,
        },
        answers: {
            type: Array,
            required: true,
        },
        disableEdit: {
            type: Boolean,
            required: true,
        },
        // Echoes the question_set's two-tier toggle. When true the renderer
        // draws the subgroup header strip between consecutive questions
        // that belong to different subgroups. False (default) preserves
        // the flat BPO-style 2-column grid.
        publishGateEnabled: {
            type: Boolean,
            default: false,
        },
        // confirmed_at of the parent section's latest confirmation, or null
        // if it has never been confirmed. Compared per-FIELD against that
        // field's own updated_on (not applied as one flat section-wide
        // flag) so editing one question in a confirmed section only
        // re-highlights that question -- the other four don't revert to
        // "review required" just because a sibling field changed. Derived
        // at render time, so it automatically re-escalates the moment an
        // edit lands after the section's last confirmation, with no
        // separate DB flag to keep in sync.
        sectionConfirmedAt: {
            type: String,
            default: null,
        },
    },

    methods: {
        // True when this item kicks off a new subgroup -- either it is the
        // first item that has a subgroup_name at all, or the previous item
        // belongs to a different subgroup_name. Returning true causes the
        // template to draw a full-width header strip ahead of the field.
        shouldShowSubgroupHeader(idx) {
            const items = this.parentItem.data;
            const current = items[idx];
            if (!current || !current.subgroup_name) return false;
            const prev = items[idx - 1];
            if (!prev || !prev.subgroup_name) return true;
            return prev.subgroup_name !== current.subgroup_name;
        },

        handleInput(fieldName, value) {
            this.$emit('update:modelValue', { [fieldName]: value });
        },

        handleValidation(isValid, fieldName) {
            this.$emit('isValid', { isValid, fieldName, categoryName: this.parentItem.category_name });
        },

        getAnswer(item) {
            for (let category of this.answers) {
                for (let field of category.data) {
                    if (field.id === item.id) {
                        return field.json_value;
                    }
                }
            }
            return null;
        },

        // Find the raw answer row (with AI provenance) that matches this
        // question. Used to compute the visual state below.
        getAnswerRow(item) {
            for (const category of this.answers) {
                for (const field of category.data) {
                    if (field.id === item.id) return field;
                }
            }
            return null;
        },

        // AI Pre-Population / edit-tracking — vendor-side render helper.
        // Returns one of:
        //   'plain'              — no border, no footer
        //   'ai-inferred'        — amber border + "review required" footer.
        //                          Covers two cases that render identically:
        //                            (a) any AI-written answer not yet
        //                                reviewed (every confidence band —
        //                                high-confidence 'ai_extracted'
        //                                renders the same as 'ai_inferred')
        //                            (b) any answer (AI or vendor-typed)
        //                                edited AFTER the section's last
        //                                confirmation — only THAT field, not
        //                                its confirmed siblings, per
        //                                editedSincePriorConfirm below
        //   'ai-confirmed'       — neutral border + "AI-filled — confirmed"
        //                          footer. An AI-written field whose value
        //                          predates the section's last confirmation
        //                          (or the section has never been edited
        //                          since) — reviewed and settled.
        //   'ai-unable'          — red border (Required + unanswered,
        //                          regardless of whether AI ever touched
        //                          the field — a required question the
        //                          ingestion pipeline never attempted is
        //                          just as blocking as one it stamped
        //                          ai_unable, so both must be visible)
        //   'dashed-recommended' — dashed border (Recommended, no answer yet)
        provenanceState(item) {
            // The whole two-tier Required-to-Publish/review-required/
            // confirmation model is SE-only. BPO's `required` flag was
            // never curated as a publishability gate, so without this
            // guard every empty required BPO field wrongly gets painted
            // red as if it were blocking a publish gate that doesn't
            // exist for BPO at all.
            if (!this.publishGateEnabled) return 'plain';

            const row = this.getAnswerRow(item);
            const src = row?.source;
            const required = item?.required === true
                || item?.required === 'true'
                || item?.required === 1;
            const hasValue = this.hasVendorAnswer(row);

            // True only when THIS field's own last write happened after the
            // section's last confirmation. Comparing per-field (not one
            // flat section-wide flag) is what keeps editing one question in
            // an already-confirmed section from re-highlighting its four
            // untouched siblings. No prior confirmation to compare against
            // (sectionConfirmedAt null, e.g. a section that's never been
            // confirmed yet) means this is always false -- nothing to
            // re-escalate relative to.
            const editedSincePriorConfirm = Boolean(
                row?.updated_on
                && this.sectionConfirmedAt
                && new Date(row.updated_on) > new Date(this.sectionConfirmedAt)
            );

            if (src === 'ai_inferred' || src === 'ai_extracted') {
                // No prior confirmation at all -- 'ai-confirmed' would be a
                // lie (nothing has been reviewed/confirmed yet). Only a
                // section with an actual sectionConfirmedAt, and this field
                // untouched since, earns the settled "AI-filled — confirmed"
                // state.
                if (!this.sectionConfirmedAt) return 'ai-inferred';
                return editedSincePriorConfirm ? 'ai-inferred' : 'ai-confirmed';
            }
            if (required && !hasValue) return 'ai-unable';
            // Manually-typed answer touched since the section was last
            // confirmed -- flag it the same way an unreviewed AI answer
            // gets flagged, so it doesn't fall through to 'plain' and go
            // unnoticed until the vendor re-confirms.
            if (hasValue && editedSincePriorConfirm) return 'ai-inferred';
            if (!hasValue && !required && !src) return 'dashed-recommended';

            return 'plain';
        },

        hasVendorAnswer(row) {
            const v = row?.json_value;
            if (v === null || v === undefined) return false;
            if (typeof v === 'string' && v.trim() === '') return false;
            if (Array.isArray(v) && v.length === 0) return false;
            if (typeof v === 'object' && Object.keys(v).length === 0) return false;
            // single_select / single_select_search / boolean_select answers are
            // wrapped as { name: <value> } even when unanswered (getAnswers()
            // maps a null server value to { name: null }, not bare null) --
            // that's a non-empty object by the check above, so without this it
            // reads as "answered" and a required-but-empty dropdown never gets
            // highlighted.
            if (typeof v === 'object' && !Array.isArray(v) && 'name' in v) {
                const name = v.name;
                return name !== null && name !== undefined && String(name).trim() !== '';
            }
            return true;
        },

        // Footer text to show below the field, if any.
        provenanceFooter(item) {
            const state = this.provenanceState(item);
            if (state === 'ai-inferred') {
                const row = this.getAnswerRow(item);
                // _locallyEdited wins even if `source` still says
                // 'ai_inferred' -- that's the pre-save server value, stale
                // the moment the vendor starts editing (source only flips
                // to 'vendor_edited' after an actual save/confirm).
                if (!row?._locallyEdited && (row?.source === 'ai_inferred' || row?.source === 'ai_extracted')) {
                    const label = row?.source_label || 'the source';
                    return `AI inferred from ${label} — review required`;
                }
                // Manually-typed answer edited since the section was last
                // confirmed -- same amber treatment, but there's no AI
                // source to cite.
                return 'Edited since last confirmation — review required';
            }
            if (state === 'ai-confirmed') {
                return 'AI-filled — confirmed';
            }
            if (state === 'ai-unable') {
                const row = this.getAnswerRow(item);
                if (row?.source === 'ai_unable') {
                    return 'AI was unable to answer — your input is required';
                }
                return 'Your input is required to publish this profile';
            }
            return null;
        },

        // Collects the set of names/keys this vendor currently has selected
        // for `item`, normalized across the four answer shapes:
        //   single_select / single_select_search: { name } | string
        //   multi_select / multi_select_search:   [{ name }, ...] | [string,...]
        //   tree_select:                          { [key]: { checked, ... } }
        currentSelectionSet(item) {
            const set = new Set();
            const value = this.getAnswer(item) ?? this.modelValue[item.id];
            if (!value) return set;
            if (item.field_type === 'tree_select') {
                if (typeof value === 'object') {
                    for (const [key, meta] of Object.entries(value)) {
                        if (meta && meta.checked) set.add(key);
                    }
                }
                return set;
            }
            const push = (v) => {
                if (v && typeof v === 'object' && v.name) set.add(v.name);
                else if (typeof v === 'string' && v) set.add(v);
            };
            if (Array.isArray(value)) value.forEach(push);
            else push(value);
            return set;
        },

        // Filters and re-labels deprecated entries in `item.dropdown_options`:
        //   - deprecated entry NOT in the vendor's current selection → hidden
        //   - deprecated entry IN the vendor's current selection → kept, with
        //     " (Deprecated)" appended to its display label (tree nodes get
        //     the suffix applied directly to `label`; flat options carry the
        //     `deprecated: true` flag through so the per-component option
        //     slot can render the suffix without touching `name`, which the
        //     save path extracts verbatim).
        prepareOptions(item) {
            const raw = item.dropdown_options;
            if (!Array.isArray(raw)) return raw;
            const currentSet = this.currentSelectionSet(item);

            const transform = (option) => {
                if (Array.isArray(option.children)) {
                    const kept = [];
                    for (const child of option.children) {
                        const t = transform(child);
                        if (t !== null) kept.push(t);
                    }
                    return { ...option, children: kept };
                }
                if (!option.deprecated) return option;
                const keyOrName = option.key ?? option.name;
                if (!currentSet.has(keyOrName)) return null;
                if (option.key !== undefined) {
                    return { ...option, label: `${option.label} (Deprecated)` };
                }
                return option;
            };

            return raw.map(transform).filter((o) => o !== null);
        },

        // Stamps `deprecated: true` onto the vendor's stored answer when it
        // matches a deprecated option, so the per-component value/chip slot
        // can render " (Deprecated)" on the selected display. The flag rides
        // along on the modelValue object but is dropped on save (which
        // serializes only `.name`). Tree answers are keyed by `key` and the
        // displayed label comes from the option lookup, so they don't need
        // this enrichment.
        decorateAnswer(item, answer) {
            if (!answer) return answer;
            if (item.field_type === 'tree_select') return answer;
            const raw = item.dropdown_options;
            if (!Array.isArray(raw)) return answer;
            const deprecatedNames = new Set();
            for (const opt of raw) {
                if (opt && opt.deprecated && opt.name) deprecatedNames.add(opt.name);
            }
            if (deprecatedNames.size === 0) return answer;
            const enrich = (v) =>
                v && typeof v === 'object' && deprecatedNames.has(v.name)
                    ? { ...v, deprecated: true }
                    : v;
            if (Array.isArray(answer)) return answer.map(enrich);
            return enrich(answer);
        },

        getAttachmentInfo(item) {
            for (let category of this.answers) {
                for (let field of category.data) {
                    if (field.id === item.id) {
                        if (field.other && typeof field.other === "object" && "attachments" in field.other) {
                            return field.other.attachments?.[0];
                        }
                        return null;
                    }
                }
            }
        },
    },
};
</script>

<style lang="scss">
/* Comfortable gutter between the two columns. PrimeFlex's default
   .p-grid -> .p-col-* padding is 0.5rem/side. We bump that to 0.75rem
   so the question/answer pairs in adjacent columns have a visible gap
   between them rather than feeling pushed together. */
.p-grid {
    margin-left: -0.75rem;
    margin-right: -0.75rem;
}
.p-grid > [class*="p-col"] {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
}

/* Inputs previously had margin-left: 1rem which indented them past
   their own labels and produced an asymmetric look (gap on the left,
   none on the right). Drop the offset so each input aligns with its
   label and fills the column edge-to-edge. */
.p-dropdown {
    max-width: 100%;
}
.p-multiselect {
    max-width: 100%;
}

.p-inputgroup {
    max-width: 100%;
}
.input-text {
    max-width: 100%;
}
.add-new-btn {
    margin-top: 1.4rem !important;
    width: 8rem !important;
    background: #00796b !important;
    border: 2px solid #00796b !important;
    display: block;
    float: right !important;
}
.p-button.p-button-outlined {
    background-color: transparent !important;
    color: #00796b !important;
    border: 1px solid;
}
.p-toolbar {
    flex-wrap: wrap;
    background-color: #d6d6d6;
    height: 3rem;
}

.p-button.p-button-icon-only.p-button-rounded {
    border-radius: 50%;
    height: 2.5rem;
    width: 2.5rem !important;
    margin: 0.5rem;
    background: #ffffff !important;
    border: none !important;
    display: flex;
    float: left !important;
}
.p-button.p-button-icon-only.p-button-rounded:hover {
    background: #e4edd9 !important;
}
.p-datatable {
    .p-datatable-thead > tr > th {
        color: #707683 !important;
        background: #fbfbfb !important;
        transition: none;
    }
}
.p-calendar-w-btn .p-datepicker-trigger.p-button {
    background-color: transparent !important;
    border: 0 none !important;
    margin: 0 !important;
}
.table-wrap {
    margin-top: 5rem;
}
.toolbar-wrap {
    width: 100%;
    height: 3rem;
    background: #e4edd9;
    align-self: center;
    padding: 0.75rem;
    border-radius: 0.25rem;
    margin-top: 2rem;
}
.field-title {
    font-size: 1.2rem;
    font-weight: 400;
    max-width: 100%;
}
.error-msg {
    margin-left: 1.25rem;
    color: red;
    font-size: 1rem;
    font-weight: 400;
}
.confirmation-content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
}
.link {
    position: relative;
    left: 1.5rem;
    top: 0.5rem;
    cursor: pointer;
}

.link a {
    color: blue;
}

.attach-file {
    color: #126a70;
    margin-left: 3rem;
}

.p-treeselect {
    max-width: 100% !important;
}

/* Subgroup header strip rendered between questions when the
   question_set has publish_gate_enabled = true. Uppercase small-caps
   label with a contextual PrimeIcon, bottom border to separate the
   header from the questions that belong to it. */
/* The wrap is just a full-width grid column; styling lives on the
   inner .subgroup-header so the band aligns with the section purpose
   strip rather than bleeding into the grid's negative side margins. */
.subgroup-header-wrap {
    margin: 1rem 0 0.5rem;
}
.subgroup-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    background: #f0fdfa;
    border-left: 4px solid #14b8a6;
    border-radius: 0 4px 4px 0;
}
.subgroup-header .subgroup-icon {
    color: #14b8a6;
    font-size: 1.35rem;
}
.subgroup-header .subgroup-label {
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    font-size: 1.05rem;
    color: #0f172a;
}

/* =========================================================================
   AI Pre-Population — vendor-side provenance render states.
   Each field slot gets one of five classes based on the row's `source`
   column plus the parent section's confirmation state:
     * answerfield-plain             — default; no visual treatment
     * answerfield-ai-inferred       — amber border + "AI inferred from X" footer
     * answerfield-ai-confirmed      — neutral grey border + "AI-filled — confirmed"
                                        footer (same source as ai-inferred, but the
                                        parent section is currently confirmed)
     * answerfield-ai-unable         — red border + "AI unable" footer (Required)
     * answerfield-dashed-recommended — dashed grey border (Recommended, unanswered)
   The border wraps the ENTIRE field (label + input + validation) so it
   reads as one unit — mirroring the spec mockup.
   ========================================================================= */
.answerfield-slot {
    /* Padding inside the border ring so inputs don't visually touch it. */
    border-radius: 8px;
    transition: box-shadow 0.15s;
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.5rem;
}
/* Plain / dashed-recommended: nothing visible unless dashed. */
.answerfield-plain {
    border: 1px solid transparent;
}
.answerfield-dashed-recommended {
    border: 1px dashed #cbd5e1;
    background: #fafbfc;
}
.answerfield-ai-inferred {
    border: 1.5px solid #d97706;
    background: #fffbeb;
}
.answerfield-ai-confirmed {
    border: 1.5px solid #94a3b8;
    background: #f8fafc;
}
.answerfield-ai-unable {
    border: 1.5px solid #dc2626;
    background: #fef2f2;
}

.answerfield-provenance-footer {
    margin-top: 0.4rem;
    font-size: 0.8rem;
    line-height: 1.35;
    display: flex;
    gap: 0.4rem;
    align-items: baseline;
}
.answerfield-footer-icon {
    font-weight: 700;
    flex-shrink: 0;
    width: 1rem;
    text-align: center;
}
.answerfield-footer-ai-inferred {
    color: #92400e;
}
.answerfield-footer-ai-confirmed {
    color: #475569;
}
.answerfield-footer-ai-unable {
    color: #991b1b;
}
</style>
